/**
 * Shared GLSL chunks for the FX system.
 * owner: fx
 *
 * Everything here is pure procedural maths — no textures are loaded from disk or
 * network anywhere in src/fx. The sprite / decal atlases are rendered by these
 * functions into WebGLRenderTargets at boot (see Textures.js).
 */

/* ------------------------------------------------------------------ *
 * hashing + noise
 * ------------------------------------------------------------------ */
export const NOISE = /* glsl */`
float hash11(float p){ p = fract(p * 0.1031); p *= p + 33.33; p *= p + p; return fract(p); }
float hash12(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * 0.1031); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.x + p3.y) * p3.z); }
float hash13(vec3 p3){ p3 = fract(p3 * 0.1031); p3 += dot(p3, p3.zyx + 31.32); return fract((p3.x + p3.y) * p3.z); }
vec2 hash22(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yzx + 33.33); return fract((p3.xx + p3.yz) * p3.zy); }
vec3 hash33(vec3 p3){ p3 = fract(p3 * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yxz + 33.33); return fract((p3.xxy + p3.yxx) * p3.zyx); }
vec3 hash32(vec2 p){ vec3 p3 = fract(vec3(p.xyx) * vec3(0.1031, 0.1030, 0.0973)); p3 += dot(p3, p3.yxz + 33.33); return fract((p3.xxy + p3.yzz) * p3.zyx); }

float vnoise2(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = hash12(i), b = hash12(i + vec2(1.0, 0.0));
  float c = hash12(i + vec2(0.0, 1.0)), d = hash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float vnoise3(vec3 p){
  vec3 i = floor(p), f = fract(p);
  vec3 u = f * f * (3.0 - 2.0 * f);
  float n000 = hash13(i + vec3(0.0,0.0,0.0)), n100 = hash13(i + vec3(1.0,0.0,0.0));
  float n010 = hash13(i + vec3(0.0,1.0,0.0)), n110 = hash13(i + vec3(1.0,1.0,0.0));
  float n001 = hash13(i + vec3(0.0,0.0,1.0)), n101 = hash13(i + vec3(1.0,0.0,1.0));
  float n011 = hash13(i + vec3(0.0,1.0,1.0)), n111 = hash13(i + vec3(1.0,1.0,1.0));
  return mix(mix(mix(n000,n100,u.x), mix(n010,n110,u.x), u.y),
             mix(mix(n001,n101,u.x), mix(n011,n111,u.x), u.y), u.z);
}

float fbm2(vec2 p, int oct, float gain){
  float a = 0.5, s = 0.0, n = 0.0;
  for (int i = 0; i < 8; i++){
    if (i >= oct) break;
    s += a * vnoise2(p); n += a; a *= gain; p *= 2.03; p += 11.7;
  }
  return s / max(n, 1e-4);
}

float fbm3(vec3 p, int oct, float gain){
  float a = 0.5, s = 0.0, n = 0.0;
  for (int i = 0; i < 8; i++){
    if (i >= oct) break;
    s += a * vnoise3(p); n += a; a *= gain; p *= 2.02; p += 7.3;
  }
  return s / max(n, 1e-4);
}

/** ridged multifractal — cracks, rust edges, fire filaments */
float ridged2(vec2 p, int oct){
  float a = 0.5, s = 0.0, n = 0.0;
  for (int i = 0; i < 8; i++){
    if (i >= oct) break;
    float v = 1.0 - abs(vnoise2(p) * 2.0 - 1.0);
    s += a * v * v; n += a; a *= 0.5; p *= 2.07; p += 3.1;
  }
  return s / max(n, 1e-4);
}

/** worley: returns (F1, F2, cellHash) */
vec3 worley2(vec2 p){
  vec2 ip = floor(p), fp = fract(p);
  float f1 = 8.0, f2 = 8.0, id = 0.0;
  for (int y = -1; y <= 1; y++){
    for (int x = -1; x <= 1; x++){
      vec2 g = vec2(float(x), float(y));
      vec2 o = hash22(ip + g);
      float d = length(g + o - fp);
      if (d < f1){ f2 = f1; f1 = d; id = hash12(ip + g + 17.0); }
      else if (d < f2){ f2 = d; }
    }
  }
  return vec3(f1, f2, id);
}

/** domain-warped fbm — the difference between "noise" and "smoke" */
float warpFbm(vec2 p, int oct){
  vec2 q = vec2(fbm2(p + vec2(0.0, 0.0), 4, 0.5), fbm2(p + vec2(5.2, 1.3), 4, 0.5));
  vec2 r = vec2(fbm2(p + 4.0 * q + vec2(1.7, 9.2), 4, 0.5), fbm2(p + 4.0 * q + vec2(8.3, 2.8), 4, 0.5));
  return fbm2(p + 4.0 * r, oct, 0.5);
}

float sat(float x){ return clamp(x, 0.0, 1.0); }
vec3  sat3(vec3 x){ return clamp(x, 0.0, 1.0); }
float remap(float x, float a, float b){ return sat((x - a) / max(b - a, 1e-5)); }
mat2 rot2(float a){ float c = cos(a), s = sin(a); return mat2(c, -s, s, c); }
`;

/* ------------------------------------------------------------------ *
 * view / depth helpers shared by particles + decals
 * ------------------------------------------------------------------ */
export const DEPTH_HELPERS = /* glsl */`
// uDepthMode 0: tDepth is FX's packed target   (rgb = view normal, a = viewZ)
// uDepthMode 1: tDepth is the render team's gData (b = viewZ)
//               tNormal is the render team's gNormal (rgb = view normal)
// Both return FX_FAR where nothing was rasterised, so callers never need a
// separate "is this the sky" test.
uniform sampler2D tDepth;
uniform sampler2D tNormal;
uniform float uDepthMode;
#define FX_FAR 1.0e6

float fxSceneZ(vec2 uv){
  float z = (uDepthMode > 0.5) ? texture2D(tDepth, uv).b : texture2D(tDepth, uv).a;
  return (z > 1.0e-3 && z < 1.0e5) ? z : FX_FAR;
}
vec3 fxSceneN(vec2 uv){
  return (uDepthMode > 0.5) ? texture2D(tNormal, uv).xyz : texture2D(tDepth, uv).xyz;
}
`;

/* ------------------------------------------------------------------ *
 * curl noise sampled from the pre-baked 2D curl field
 * ------------------------------------------------------------------ */
export const CURL = /* glsl */`
uniform sampler2D tCurl;
// Tri-planar-ish sampling of a divergence-free 2D field gives a convincing,
// cheap 3D curl for turbulence. 3 fetches instead of 18 noise evaluations.
vec3 curlNoise(vec3 p, float scale, float t){
  vec3 q = p * scale;
  vec3 a = texture2D(tCurl, q.xy * 0.5 + vec2(t * 0.031, t * 0.017)).xyz * 2.0 - 1.0;
  vec3 b = texture2D(tCurl, q.yz * 0.5 + vec2(t * 0.023, -t * 0.029)).xyz * 2.0 - 1.0;
  vec3 c = texture2D(tCurl, q.zx * 0.5 + vec2(-t * 0.019, t * 0.037)).xyz * 2.0 - 1.0;
  return vec3(a.x + b.z + c.y, a.y + b.x + c.z, a.z + b.y + c.x) * 0.4714;
}
`;
